import React from 'react';
import { usePermissionContext } from '@contexts/PermissionContext';
import PermissionDenied from './PermissionDenied';
import PermissionLoader from './PermissionLoader';

/**
 * PermissionGate - Controls access based on user permissions
 * 
 * Note: Pro users automatically bypass all permission checks.
 * Permission enforcement is only for Upercut users.
 */
interface PermissionGateProps {
    permission?: string;
    permissions?: string[];
    requireAll?: boolean;
    fallback?: React.ReactNode;
    children: React.ReactNode;
}

const PermissionGate: React.FC<PermissionGateProps> = ({
    permission,
    permissions,
    requireAll = false,
    fallback,
    children,
}) => {
    const { hasAnyPermission, hasAllPermissions, loading } = usePermissionContext();

    if (loading) {
        return <PermissionLoader />;
    }

    const requiredPermissions = permission ? [permission] : permissions || [];
    if (requiredPermissions.length === 0) {
        return <>{children}</>;
    }

    const allowed = requireAll
        ? hasAllPermissions(requiredPermissions)
        : hasAnyPermission(requiredPermissions);

    if (!allowed) {
        return <>{fallback || <PermissionDenied />}</>;
    }

    return <>{children}</>;
};

export default PermissionGate;
